import clsx from 'clsx';
import {
  forwardRef,
  type ReactNode,
  ElementType,
  type ReactElement,
  Children,
} from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  type PolymorphicComponentProps,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _useAriaLabelingProps as useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';

import {
  avatarAbbreviationCSS,
  avatarDisabledCSS,
  avatarWrapperCSS,
} from './Avatar.sty.js';
import { Label } from './Label.js';
import { Subtitle } from './Subtitle.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { _sanitizeSymbol } from '../../core/utils/sanitize-symbol.js';

// Merge own props with others inherited from the underlying element type
/** @public */
export type AvatarProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  AvatarOwnProps
>;

/**
 * Accepted properties for the Avatar component
 * @public
 */
export interface AvatarOwnProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * The size of the Avatar.
   * @defaultValue 'default'
   */
  size?: 'default' | 'small';

  /**
   * The letters displayed in the colored circle of the Avatar.
   */
  abbreviation: string;

  /**
   * Whether the Avatar is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
}

type AvatarSlots = {
  label?: ReactNode;
  subtitle?: ReactNode;
};

/** Iterates over child nodes to extract specific component slots. */
function getAvatarSlots(children: ReactNode) {
  const slots: AvatarSlots = {};

  Children.forEach(children, (child) => {
    if (isElement(child)) {
      switch (child.type) {
        case Label:
          slots.label = child;
          break;
        case Subtitle:
          slots.subtitle = child;
          break;
      }
    }
  });

  return slots;
}

/**
 * Avatar component
 */
const InternalAvatar: <E extends ElementType = 'div'>(
  props: AvatarProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(props: AvatarProps<E>, ref: typeof props.ref) => {
    const {
      children,
      size = 'default',
      abbreviation,
      disabled = false,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      as,
      ...remainingProps
    }: AvatarProps<E> = props;
    const slots = getAvatarSlots(children);
    const ariaLabelingProps = useAriaLabelingProps(remainingProps);

    const { focusProps, focusClassName } = useFocusRing({ disabled });

    const Abbreviation = as ?? 'div';

    // ensuring that 'rovingFocusElement' remains assigned to the Abbrevation Element for focus functionality.
    const remainingConsumerClassname = (consumerClassName ?? '').replace(
      /rovingFocusElement/g,
      '',
    );
    const hasRovingFocus = (consumerClassName ?? '').includes(
      'rovingFocusElement',
    );
    return (
      <div
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(
          avatarWrapperCSS,
          textStyleCSS({ textStyle: 'base' }),
          remainingConsumerClassname,
        )}
        style={consumerStyle}
      >
        <Flex gap={size === 'small' ? 4 : 8} alignItems="center" minWidth={0}>
          <Abbreviation
            ref={ref}
            className={clsx(
              {
                [avatarDisabledCSS]: disabled,
                rovingFocusElement: hasRovingFocus,
              },
              avatarAbbreviationCSS({ size, disabled }),
              textStyleCSS({ textStyle: size === 'small' ? 'small' : 'base' }),
              focusClassName,
            )}
            {...focusProps}
            {...ariaLabelingProps}
            {...remainingProps}
          >
            {_sanitizeSymbol(abbreviation, size === 'default' ? 2 : undefined)}
          </Abbreviation>
          {(slots.label || slots.subtitle) && (
            <Flex flexDirection="column" gap={0} minWidth={0}>
              {slots.label}
              {slots.subtitle}
            </Flex>
          )}
        </Flex>
      </div>
    );
  },
);

(
  InternalAvatar as typeof InternalAvatar & { displayName: string }
).displayName = 'Avatar';

/**
 * The `Avatar` component can be used as a visual representation of a user.
 * @public
 */
export const Avatar = Object.assign(InternalAvatar, {
  Label,
  Subtitle,
});
